<template>
  <div>
    <div
      v-if="hasDemoVideos"
      class="col-12"
    >
      <program-video
        v-if="currentDemoVideo"
        :video-info="currentDemoVideo"
        :max-seek="videoMaxSeek"
        :time-update-interval="videoTimeUpdateInterval"
        :session-paused="sessionPaused"
        @time-update="onVideoTimeUpdate"
        @video-completed="onVideoCompleted"
      />
    </div>
    <template
      v-if="hasMultipleDemoVideos"
    >
      <div
        v-for="demoVideoItem in visibleDemoVideos"
        :key="demoVideoItem.src"
        class="col-4"
      >
        <q-img
          :src="demoVideoItem.coverphoto || 'program-placeholder.png'"
          :ratio="16/9"
          class="cursor-pointer"
          @click="onVideoChange(demoVideoItem)"
        />

        <div
          v-if="demoVideoItem === currentDemoVideo"
          class="bg-red-7 q-pa-xs text-white ellipsis"
        >
          {{ $t('programs.programInfo.videoNowPlaying') }}
        </div>
      </div>
    </template>
    <div class="row col-12 q-mt-md">
      <div
        v-for="(programAutomationAction, index) in normalProgramAutomationActions"
        :key="index"
        class="col"
      >
        <automation-action
          :automation-action="programAutomationAction"
          :disable="disableAutomationActions"
          class="col"
          @done="onCompleteAutomationAction"
        />
      </div>
    </div>
  </div>
</template>
<script>
import AutomationAction from 'components/Programs/Automation/AutomationAction';
import ProgramVideo from 'components/Programs/ProgramVideo';
import {
  get,
  head, isEmpty, reject, size,
} from 'lodash';
import { mapState } from 'pinia';
import ProgramAutomationActionsDB from 'src/fb/programAutomationActions-db';
import ProgramFilesDB from 'src/fb/programFiles-db';
import { auth } from 'src/store/auth';

export default {
  name: 'ProgramInstructions',

  components: {
    AutomationAction,
    ProgramVideo,
  },

  props: {
    program: {
      type: Object,
      required: true,
    },
    disableAutomationActions: {
      type: Boolean,
      required: false,
    },
    videoMaxSeek: {
      type: Number,
      default: null,
    },
    videoTimeUpdateInterval: {
      type: Number,
      default: 1,
    },
    sessionPaused: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['automation-action-completed', 'video-time-update', 'video-completed'],

  data() {
    return {
      loadingSpinner: false,

      demoVideos: [],
      currentDemoVideo: {},

      programAutomationActions: [],

    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isManagerOrAdministrator']),

    visibleDemoVideos() {
      const userRole = get(this.userDetails, 'role', '');

      return this.isManagerOrAdministrator
        ? this.demoVideos
        : this.demoVideos.filter((demoVideo) => (
          !demoVideo.limitedVisibility
          || (demoVideo.visibility.includes(userRole))
        ));
    }, // visibleDemoVideos

    hasDemoVideos() {
      return size(this.visibleDemoVideos) > 0;
    }, // hasDemoVideos()

    hasMultipleDemoVideos() {
      return size(this.visibleDemoVideos) > 1;
    }, // hasMultipleDemoVideos

    normalProgramAutomationActions() {
      return reject(
        this.programAutomationActions,
        (programAutomationAction) => !!programAutomationAction.buttonType,
      );
    }, // normalProgramAutomationActions()

  },

  watch: {
    'program.id': {
      immediate: true,
      handler(newProgramId) {
        if (newProgramId) {
          this.reloadDemoVideos(this.program);
          this.reloadProgramAutomationActions(this.program);
        }
      },
    },
  },

  methods: {

    onCompleteAutomationAction(automationActionResults) {
      this.$emit('automation-action-completed', automationActionResults);
    }, // onCompleteAutomationAction()

    onVideoTimeUpdate(currentTime) {
      this.$emit('video-time-update', currentTime);
    },

    onVideoCompleted() {
      this.$emit('video-completed');
    },

    async reloadDemoVideos(program) {
      if (!program || isEmpty(program)) {
        return;
      }
      this.loadingSpinner = true;
      try {
        const programFilesDb = new ProgramFilesDB(program.id);
        this.demoVideos = await programFilesDb.readAll([['programFileType', '==', 'instructionsVideo']], 'order');
      } finally {
        this.loadingSpinner = false;
      }
      this.currentDemoVideo = head(this.visibleDemoVideos);
    }, // reloadDemoVideos

    async reloadProgramAutomationActions(program) {
      if (!program || isEmpty(program)) {
        return;
      }

      try {
        const programAutomationActionsDb = new ProgramAutomationActionsDB(program.id);
        this.programAutomationActions = await programAutomationActionsDb.readAll(null, 'order');
      } catch {
        this.programAutomationActions = [];
      }
    }, // reloadProgramAutomationActions

    onVideoChange(selectedVideo) {
      this.currentDemoVideo = selectedVideo;
    }, // onVideoChange()
  },
};
</script>
